/**
 * Typed, validated access to the console's environment.
 *
 * Two sources, in this order of precedence:
 *
 *  1. **Runtime config** (`GET /config.json`, served by the app itself on dev and ini): the issuer, the
 *     client id and the API base, so ONE environment-agnostic build serves every environment.
 *     {@link loadRuntimeConfig} fetches it once, before the app renders (`main.tsx` waits for it).
 *  2. **Baked `VITE_*` values**, read exactly once, here. Nothing else touches `import.meta.env`.
 *     They are the fallback for every key the runtime config does not supply, which keeps the Vite dev
 *     server, the Playwright prebuilt bundle and qa/prod (S3 + CloudFront, no `/config.json`) working
 *     unchanged.
 *
 * `env` is one object that {@link loadRuntimeConfig} updates in place, so everything that imports it
 * sees the final values. Read it inside functions and components, never at module load.
 */

function optional(value: string | undefined): string | undefined {
  const trimmed = value?.trim()
  return trimmed === undefined || trimmed.length === 0 ? undefined : trimmed
}

function withDefault(value: string | undefined, fallback: string): string {
  return optional(value) ?? fallback
}

function stripTrailingSlash(value: string): string {
  return value.endsWith('/') ? value.slice(0, -1) : value
}

const origin = typeof window === 'undefined' ? '' : window.location.origin

/**
 * Root the server's PUBLIC, non-`/api` routes live at — today just `BridgeController`'s
 * `/tooling/info` and `/tooling/bridge-<version>.tgz`, deliberately outside `/api` (CB-WI9: an
 * installer has a zone token and no console session, so the route it fetches cannot require one).
 *
 * A same-origin deployment (`apiBaseUrl` left at its default `/api`) resolves to the page's own
 * origin, since the browser already talks to that server. A split deployment's absolute
 * `apiBaseUrl` keeps that origin and drops the `/api` suffix, because `/tooling/**` sits beside
 * it, not under it — a bare `/tooling/info` would otherwise resolve against the CONSOLE's origin,
 * not the API's.
 */
function toolingRoot(base: string, pageOrigin: string): string {
  if (/^https?:\/\//i.test(base)) {
    try {
      return new URL(base).origin
    } catch {
      return pageOrigin
    }
  }
  return pageOrigin
}

export interface AppEnv {
  readonly apiBaseUrl: string
  readonly toolingRootUrl: string
  readonly keycloakIssuer: string | undefined
  readonly keycloakClientId: string | undefined
  readonly keycloakScope: string
  readonly redirectUri: string
  readonly postLogoutRedirectUri: string
  readonly environment: string | undefined
}

/** The `VITE_*` values as the build inlined them (all optional, all raw strings). */
export interface BakedEnv {
  readonly VITE_API_BASE_URL?: string
  readonly VITE_KEYCLOAK_ISSUER?: string
  readonly VITE_KEYCLOAK_CLIENT_ID?: string
  readonly VITE_KEYCLOAK_SCOPE?: string
  readonly VITE_KEYCLOAK_REDIRECT_URI?: string
  readonly VITE_KEYCLOAK_POST_LOGOUT_REDIRECT_URI?: string
  readonly VITE_ENVIRONMENT?: string
}

/** What `/config.json` may supply; each key is independent and validated on its own. */
export interface RuntimeConfig {
  readonly issuer?: string
  readonly clientId?: string
  readonly apiBaseUrl?: string
}

const ABSOLUTE_URL = /^https?:\/\/\S+$/i

function readBaked(): BakedEnv {
  return {
    VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
    VITE_KEYCLOAK_ISSUER: import.meta.env.VITE_KEYCLOAK_ISSUER,
    VITE_KEYCLOAK_CLIENT_ID: import.meta.env.VITE_KEYCLOAK_CLIENT_ID,
    VITE_KEYCLOAK_SCOPE: import.meta.env.VITE_KEYCLOAK_SCOPE,
    VITE_KEYCLOAK_REDIRECT_URI: import.meta.env.VITE_KEYCLOAK_REDIRECT_URI,
    VITE_KEYCLOAK_POST_LOGOUT_REDIRECT_URI: import.meta.env.VITE_KEYCLOAK_POST_LOGOUT_REDIRECT_URI,
    VITE_ENVIRONMENT: import.meta.env.VITE_ENVIRONMENT,
  }
}

/**
 * Validates a parsed `/config.json`. `undefined` when it is not an object or supplies nothing usable
 * (so the caller keeps the baked values); otherwise only the keys that are valid, so a partial or
 * partly-bad document still helps for what it got right.
 *
 * Wire keys are snake_case (`issuer`, `client_id`, `api_base_url`), like the rest of the API.
 * `issuer` must be an absolute http(s) URL; `api_base_url` an absolute http(s) URL or a path starting
 * with `/`; `client_id` any non-blank string without whitespace. Anything else is dropped.
 */
export function parseRuntimeConfig(raw: unknown): RuntimeConfig | undefined {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return undefined
  }
  const record = raw as Record<string, unknown>
  const text = (key: string): string | undefined => {
    const value = record[key]
    return typeof value === 'string' ? optional(value) : undefined
  }

  const issuer = text('issuer')
  const clientId = text('client_id')
  const apiBaseUrl = text('api_base_url')
  const config: { issuer?: string; clientId?: string; apiBaseUrl?: string } = {}
  if (issuer !== undefined && ABSOLUTE_URL.test(issuer)) {
    config.issuer = issuer
  }
  if (clientId !== undefined && !/\s/.test(clientId)) {
    config.clientId = clientId
  }
  if (apiBaseUrl !== undefined && (ABSOLUTE_URL.test(apiBaseUrl) || /^\/\S*$/.test(apiBaseUrl))) {
    config.apiBaseUrl = apiBaseUrl
  }
  return Object.keys(config).length === 0 ? undefined : config
}

/**
 * The environment from the baked values, with the runtime config laid over the three keys it may
 * supply. Pure: no `window`, no `import.meta.env` (the caller passes both), so it tests without either.
 */
export function resolveEnv(
  baked: BakedEnv,
  runtime: RuntimeConfig | undefined,
  pageOrigin: string,
): AppEnv {
  const apiBaseUrl = stripTrailingSlash(
    withDefault(runtime?.apiBaseUrl ?? baked.VITE_API_BASE_URL, '/api'),
  )
  const issuer = optional(runtime?.issuer ?? baked.VITE_KEYCLOAK_ISSUER)
  return {
    apiBaseUrl,
    toolingRootUrl: toolingRoot(apiBaseUrl, pageOrigin),
    keycloakIssuer: issuer === undefined ? undefined : stripTrailingSlash(issuer),
    keycloakClientId: optional(runtime?.clientId ?? baked.VITE_KEYCLOAK_CLIENT_ID),
    keycloakScope: withDefault(baked.VITE_KEYCLOAK_SCOPE, 'openid profile email'),
    redirectUri: withDefault(baked.VITE_KEYCLOAK_REDIRECT_URI, `${pageOrigin}/auth/callback`),
    postLogoutRedirectUri: withDefault(baked.VITE_KEYCLOAK_POST_LOGOUT_REDIRECT_URI, `${pageOrigin}/`),
    environment: optional(baked.VITE_ENVIRONMENT),
  }
}

type MutableEnv = { -readonly [K in keyof AppEnv]: AppEnv[K] }

const current: MutableEnv = resolveEnv(readBaked(), undefined, origin)

/** The live environment. Updated in place by {@link loadRuntimeConfig}; read it lazily. */
export const env: AppEnv = current

/** How long the app waits for `/config.json` before it starts with the baked values. */
const CONFIG_TIMEOUT_MS = 3000

/**
 * Fetches `/config.json` and applies it to {@link env}. **Never throws and never blocks past the
 * timeout**: a missing file (qa/prod's S3 origin, the Vite dev server answering `index.html`), a
 * non-JSON answer, a bad document or a network error all leave the baked values in force, which is
 * the whole fallback contract. `main.tsx` awaits this before rendering, so no component ever sees
 * the baked issuer and then a different one.
 *
 * @returns what was applied, or `undefined` when the baked values stayed.
 */
export async function loadRuntimeConfig(
  fetchImpl: typeof fetch = (input, init) => globalThis.fetch(input, init),
  url = '/config.json',
): Promise<RuntimeConfig | undefined> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), CONFIG_TIMEOUT_MS)
  try {
    const response = await fetchImpl(url, {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    })
    if (!response.ok || !/json/i.test(response.headers.get('content-type') ?? '')) {
      return undefined
    }
    const runtime = parseRuntimeConfig(await response.json())
    if (runtime !== undefined) {
      Object.assign(current, resolveEnv(readBaked(), runtime, origin))
    }
    return runtime
  } catch {
    return undefined
  } finally {
    clearTimeout(timer)
  }
}

/**
 * True when both Keycloak variables are present, i.e. login is actually usable.
 * Screens degrade to an explicit configuration error rather than a blank page.
 */
export function isAuthConfigured(): boolean {
  return env.keycloakIssuer !== undefined && env.keycloakClientId !== undefined
}

export interface OidcConfig {
  readonly issuer: string
  readonly clientId: string
  readonly scope: string
  readonly redirectUri: string
  readonly postLogoutRedirectUri: string
}

/** Narrowed config, or `undefined` when the app is not configured for auth. */
export function oidcConfig(): OidcConfig | undefined {
  const { keycloakIssuer, keycloakClientId } = env
  if (keycloakIssuer === undefined || keycloakClientId === undefined) {
    return undefined
  }
  return {
    issuer: keycloakIssuer,
    clientId: keycloakClientId,
    scope: env.keycloakScope,
    redirectUri: env.redirectUri,
    postLogoutRedirectUri: env.postLogoutRedirectUri,
  }
}
